import { css } from '@emotion/react';

import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { Spacing } from 'chatie-design-system';

import { getCharacterDetail } from '@api/chatbot';
import { ICommunityFeedMentionPreview } from '@api/community';
import { fetchStory } from '@api/story';
import { fetchUser } from '@api/user';
import { QUERY_KEYS } from '@constants';
import {
  IChatCharacter,
  IEFinderObject,
  IStoryDetail,
  IUserDetail,
} from '@model';
import { ParsedMention, extractUniqueMentions } from '@utils/mention';

import MentionCard, {
  MentionCardModel,
  createCharacterMentionCardModel,
  createStoryMentionCardModel,
  createUserMentionCardModel,
} from './MentionCard';

const listCSS = css`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xsmall}px;
  margin-top: ${Spacing.small}px;

  &:empty {
    display: none;
    margin-top: 0;
  }
`;

type MentionCardsProps =
  | {
      source: 'content';
      content: string;
      className?: string;
    }
  | {
      source: 'previews';
      previews: ICommunityFeedMentionPreview[];
      className?: string;
    };

type ContentMentionCardsProps = {
  content: string;
  className?: string;
};

type PreviewMentionCardsProps = {
  previews: ICommunityFeedMentionPreview[];
  className?: string;
};

type MentionCardItemProps = {
  mention: ParsedMention;
};

type PreviewMentionCardItemProps = {
  preview: ICommunityFeedMentionPreview;
};

const getEFinderImage = (
  image?: Partial<IEFinderObject<'image'>> | null
): IEFinderObject<'image'> | undefined => {
  if (!image?.key || !image.attachment) {
    return undefined;
  }
  return image as IEFinderObject<'image'>;
};

const MentionCardItem = ({ mention }: MentionCardItemProps) => {
  const userQuery = useQuery<IUserDetail | undefined>(
    QUERY_KEYS.USER.ONE(mention.id),
    () => fetchUser(mention.id),
    {
      enabled: mention.type === 'u',
      suspense: false,
      useErrorBoundary: false,
      retry: false,
      staleTime: 60 * 1000,
    }
  );

  const storyQuery = useQuery<IStoryDetail>(
    QUERY_KEYS.STORY.ONE(mention.id),
    () => fetchStory(mention.id),
    {
      enabled: mention.type === 's',
      suspense: false,
      useErrorBoundary: false,
      retry: false,
      staleTime: 60 * 1000,
    }
  );

  const characterQuery = useQuery<IChatCharacter | undefined>(
    QUERY_KEYS.CHARACTER.ONE(mention.id),
    async () => {
      const data = await getCharacterDetail(mention.id);
      return data as IChatCharacter | undefined;
    },
    {
      enabled: mention.type === 'c',
      staleTime: 60 * 1000,
    }
  );

  const model = useMemo(() => {
    if (mention.type === 'u') {
      const user = userQuery.data;
      if (!user) {
        return null;
      }

      return createUserMentionCardModel(user);
    }

    if (mention.type === 's') {
      const story = storyQuery.data;
      if (!story) {
        return null;
      }

      return createStoryMentionCardModel({
        ...story,
        authorNickname: story.user.nickname,
      });
    }

    const character = characterQuery.data as IChatCharacter | undefined;
    if (!character) {
      return null;
    }

    return createCharacterMentionCardModel({
      ...character,
      image: character.profile_image || character.cover_image,
      messageCount: character.stat?.message_count || 0,
      likeCount: character.stat?.like_count || 0,
    });
  }, [characterQuery.data, mention.type, storyQuery.data, userQuery.data]);

  return model ? <MentionCard model={model} /> : null;
};

const PreviewMentionCardItem = ({ preview }: PreviewMentionCardItemProps) => {
  let model: MentionCardModel;

  switch (preview.type) {
    case 'user':
      model = createUserMentionCardModel({
        ...preview,
        picture: getEFinderImage(preview.picture),
      });
      break;
    case 'story':
      model = createStoryMentionCardModel({
        ...preview,
        coverImage: getEFinderImage(preview.coverImage),
        authorNickname: preview.user.nickname,
      });
      break;
    case 'character':
      model = createCharacterMentionCardModel({
        ...preview,
        image:
          getEFinderImage(preview.profileImage) ||
          getEFinderImage(preview.coverImage),
        messageCount: preview.stat.messageCount,
        likeCount: preview.stat.likeCount,
      });
      break;
  }

  return <MentionCard model={model} />;
};

const ContentMentionCards = ({
  content,
  className,
}: ContentMentionCardsProps) => {
  const mentions = useMemo(() => extractUniqueMentions(content), [content]);
  if (!mentions.length) {
    return null;
  }

  return (
    <div css={listCSS} className={className}>
      {mentions.map((mention) => (
        <MentionCardItem
          key={`${mention.type}:${mention.id}`}
          mention={mention}
        />
      ))}
    </div>
  );
};

const PreviewMentionCards = ({
  previews,
  className,
}: PreviewMentionCardsProps) => {
  if (!previews.length) {
    return null;
  }

  return (
    <div css={listCSS} className={className}>
      {previews.map((preview) => (
        <PreviewMentionCardItem
          key={`${preview.type}:${preview.id}`}
          preview={preview}
        />
      ))}
    </div>
  );
};

const MentionCards = (props: MentionCardsProps) => {
  if (props.source === 'previews') {
    return (
      <PreviewMentionCards
        previews={props.previews}
        className={props.className}
      />
    );
  }

  return (
    <ContentMentionCards content={props.content} className={props.className} />
  );
};

export default MentionCards;
